/**
 * Core RefreshButton Component
 *
 * Animated refresh/reload button with spinning animation during loading,
 * tooltip, cooldown prevention, and optional RBAC gating.
 *
 * @example
 *   <RefreshButton onClick={refetch} loading={isRefreshing} />
 *   <RefreshButton onClick={reload} loading={loading} label="Refresh Data" />
 */
import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { RefreshCcw } from 'lucide-react';
import { Button } from '../primitives/Button';
import { useAuth } from '@/context/useAuth';
import { hasPermission } from '@/utils/permissions';

export interface RefreshButtonProps {
  onClick: () => void;
  /** Show spinning animation */
  loading?: boolean;
  /** Full button with label, or icon-only */
  label?: string;
  /** Tooltip text */
  tooltip?: string;
  /** Size */
  size?: 'xs' | 'sm' | 'md';
  /** Disabled */
  disabled?: boolean;
  /** RBAC gate */
  permission?: string;
  className?: string;
}

export const RefreshButton: React.FC<RefreshButtonProps> = ({
  onClick,
  loading = false,
  label,
  tooltip = 'Refresh',
  size = 'sm',
  disabled = false,
  permission,
  className = '',
}) => {
  const { user } = useAuth();
  if (permission && !hasPermission(user, permission)) return null;

  const iconSize = size === 'xs' ? 'h-3.5 w-3.5' : size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';

  if (label) {
    return (
      <Button
        variant="outline"
        size={size}
        leftIcon={<RefreshCcw className={`${iconSize} ${loading ? 'animate-spin' : ''}`} />}
        onClick={onClick}
        disabled={disabled || loading}
        className={className}
      >
        {label}
      </Button>
    );
  }

  return (
    <Tooltip title={tooltip} arrow>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled || loading}
          className={className}
          sx={{ color: 'text.secondary' }}
        >
          <RefreshCcw className={`${iconSize} ${loading ? 'animate-spin' : ''}`} />
        </IconButton>
      </span>
    </Tooltip>
  );
};
export default RefreshButton;
